import { z } from 'zod';

import i18n from '@/lib/i18n';
import {
  VERIFICATION_INFORMATION_SUPPORTING_INFORMATION_ANSWERS,
  VERIFICATION_INFORMATION_SUPPORTING_INFORMATION_FIELD_ANSWER_KEYS,
  type VerificationInformationSupportingInformationAnswers,
  type VerificationInformationSupportingInformationAnswerValue,
  type VerificationInformationSupportingInformationField,
} from '@/services/api/profile';
import type { DefinedPartial } from '@/types/generics';

export const generateVerificationInformationSupportingInformationSchema = (requiredFields: VerificationInformationSupportingInformationField[]) => {
  return z.object({
    ...requiredFields.reduce(
      (acc, field) => {
        acc[field] = z.enum(VERIFICATION_INFORMATION_SUPPORTING_INFORMATION_ANSWERS, { error: i18n.t('requiredField.error') });

        return acc;
      },
      {} as Record<VerificationInformationSupportingInformationField, z.ZodType<VerificationInformationSupportingInformationAnswerValue>>,
    ),
  });
};

export const verificationInformationSupportingInformationAnswersToSupportingInformation = (
  requiredFields: VerificationInformationSupportingInformationField[],
  answers?: VerificationInformationSupportingInformationAnswers,
) => {
  const schema = generateVerificationInformationSupportingInformationSchema(requiredFields).partial();
  const supportingInformation = requiredFields.reduce(
    (acc, field) => {
      acc[field] = answers?.[VERIFICATION_INFORMATION_SUPPORTING_INFORMATION_FIELD_ANSWER_KEYS[field]];

      return acc;
    },
    {} as z.infer<typeof schema>,
  );

  return schema.parse(supportingInformation);
};

export const supportingInformationToVerificationInformationSupportingInformationAnswers = (
  supportingInformation: DefinedPartial<z.infer<ReturnType<typeof generateVerificationInformationSupportingInformationSchema>>>,
) => {
  return (Object.entries(supportingInformation) as [VerificationInformationSupportingInformationField, VerificationInformationSupportingInformationAnswerValue][]).reduce(
    (acc, [field, answer]) => {
      acc[VERIFICATION_INFORMATION_SUPPORTING_INFORMATION_FIELD_ANSWER_KEYS[field]] = answer;

      return acc;
    },
    {} as VerificationInformationSupportingInformationAnswers,
  );
};

export type VerificationInformationSupportingInformationOrDefaults = ReturnType<typeof verificationInformationSupportingInformationAnswersToSupportingInformation>;
